import { createContext, useContext, useEffect, useMemo, useState } from "react";
import * as BASE from "@/data/content";
import en from "./en";
import uk from "./uk";
import ru from "./ru";
import ar from "./ar";

export const LANGS = [
  { code: "en", label: "English" },
  { code: "uk", label: "Українська" },
  { code: "ru", label: "Русский" },
  { code: "ar", label: "العربية" },
];

const DICTS = { en, uk, ru, ar };

const isObj = (v) => v !== null && typeof v === "object" && !Array.isArray(v);

function merge(base, override) {
  if (override === undefined || override === null) return base;
  if (Array.isArray(base) && Array.isArray(override)) {
    return base.map((item, i) => (i < override.length ? merge(item, override[i]) : item));
  }
  if (isObj(base) && isObj(override)) {
    const out = { ...base };
    Object.keys(override).forEach((k) => {
      out[k] = merge(base[k], override[k]);
    });
    return out;
  }
  return override;
}

const lookup = (obj, path) => path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);

const LangContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem("vss-lang") || "en";
    } catch {
      return "en";
    }
  });

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    try {
      localStorage.setItem("vss-lang", lang);
    } catch {}
  }, [lang]);

  const value = useMemo(() => {
    const dict = DICTS[lang] || en;
    const strings = merge(en.strings, dict.strings);
    const t = (path) => {
      const v = lookup(strings, path);
      return v === undefined ? path : v;
    };
    const d = dict.data || {};
    const c = {
      services: merge(BASE.SERVICES, d.services),
      plans: merge(BASE.PACKAGES, d.plans),
      steps: merge(BASE.STEPS, d.steps),
      why: merge(BASE.WHY_POINTS, d.why),
      clients: merge(BASE.CLIENT_TYPES, d.clients),
      faqs: merge(BASE.FAQS, d.faqs),
      testimonials: merge(BASE.TESTIMONIALS, d.testimonials),
      loyaltyTiers: merge(BASE.LOYALTY_TIERS, d.loyaltyTiers),
      loyaltyServices: merge(BASE.LOYALTY_SERVICES, d.loyaltyServices),
      marquee: merge(BASE.MARQUEE_ITEMS, d.marquee),
      comparison: merge(BASE.COMPARISON, d.comparison),
      aboutChapters: d.aboutChapters,
      aboutValues: d.aboutValues,
    };
    return { lang, setLang, t, c };
  }, [lang]);

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
};

export const useLang = () => useContext(LangContext);
